import type { PremiumEligibility } from "./premium-eligibility.ts";

/**
 * What a page needs before it can render. A page declares only its own minimum:
 * each requirement is self-contained and also answers for the facts it depends
 * on, so a prerequisite is never repeated in a page's list.
 *
 * | Requirement        | Satisfied when                            | Redirect when not              |
 * |--------------------|-------------------------------------------|--------------------------------|
 * | offer_code         | a validated offer code is in session      | offer_code                     |
 * | anonymous          | no authorization token                    | already_authenticated          |
 * | authentication     | an authorization token is present         | authentication                 |
 * | onboarding         | the account has a family and accepted terms | authentication, family, terms |
 * | eligible_for_offer | Premium eligibility is eligible           | authentication, active_premium |
 * | active_premium     | Premium eligibility is active             | authentication, checkout       |
 *
 * An unreadable Premium status is a server failure, not a journey state: the error
 * surfaces through the standard API error handling and no page renders.
 */
export type PageRequirement =
  | "offer_code"
  | "anonymous"
  | "authentication"
  | "onboarding"
  | "eligible_for_offer"
  | "active_premium";

export type JourneyFacts = {
  hasOfferCode: boolean;
  isAuthenticated: boolean;
  hasFamilies?: boolean;
  termsChecked?: boolean;
  eligibility?: PremiumEligibility;
};

export type RequirementResolution =
  | { status: "satisfied" }
  | { status: "redirect"; to: UnsatisfiedRequirement };

/**
 * The requirement that blocked the page. The caller maps it to the URL that lets
 * the customer obtain it.
 */
export type UnsatisfiedRequirement =
  | "offer_code"
  | "authentication"
  | "already_authenticated"
  | "family"
  | "terms"
  | "active_premium"
  | "checkout";

const NEEDS_ACCOUNT: readonly PageRequirement[] = [
  "authentication",
  "onboarding",
  "eligible_for_offer",
  "active_premium"
];

/** Which facts have to be read from the server before the requirements can be resolved. */
export function getFactsToCollect(requirements: readonly PageRequirement[]) {
  return {
    authentication: requirements.some(requirement => NEEDS_ACCOUNT.includes(requirement)),
    onboarding: requirements.includes("onboarding"),
    eligibility: requirements.includes("eligible_for_offer") || requirements.includes("active_premium")
  };
}

export function resolvePageRequirements(
  requirements: readonly PageRequirement[],
  facts: JourneyFacts
): RequirementResolution {
  for (const requirement of requirements) {
    const unsatisfied = resolveRequirement(requirement, facts);
    if (unsatisfied) return { status: "redirect", to: unsatisfied };
  }
  return { status: "satisfied" };
}

function resolveRequirement(
  requirement: PageRequirement,
  facts: JourneyFacts
): UnsatisfiedRequirement | undefined {
  switch (requirement) {
    case "offer_code":
      return facts.hasOfferCode ? undefined : "offer_code";
    case "anonymous":
      // Offering a sign-in/sign-up choice to someone already signed in is a dead end.
      return facts.isAuthenticated ? "already_authenticated" : undefined;
    case "authentication":
      return facts.isAuthenticated ? undefined : "authentication";
    case "onboarding":
      // A Home Hub activation needs a family space and accepted terms before the
      // offer can be bound to the account.
      if (!facts.isAuthenticated) return "authentication";
      if (!facts.hasFamilies) return "family";
      if (!facts.termsChecked) return "terms";
      return undefined;
    case "eligible_for_offer":
      // Any active Premium must be settled first, otherwise the bundled offer
      // would overlap an existing subscription.
      return resolveEligibility(facts, "eligible", "active_premium");
    case "active_premium":
      return resolveEligibility(facts, "active", "checkout");
  }
}

function resolveEligibility(
  facts: JourneyFacts,
  expected: PremiumEligibility["status"],
  otherwise: UnsatisfiedRequirement
): UnsatisfiedRequirement | undefined {
  if (!facts.isAuthenticated) return "authentication";
  return facts.eligibility?.status === expected ? undefined : otherwise;
}
